/**
 * Uuid de joueur : fonctions pures, sans I/O ni store.
 *
 * Jusqu'à la v1.0.12, le satellite gardait dans son roster (PlayerRoster, StaffMembers) l'uuid écrit
 * par le PlayersModule avec les octets de chaque moitié de 8 octets inversés : la ProfileAPI et
 * LuckPerms ne le connaissent pas. La v1.0.13 et la migration des uuid le remettent dans l'ordre.
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * Uuid aux octets de chaque moitié de 8 octets inversés, en minuscules (l'opération est sa propre
 * inverse) ; null si la chaîne n'est pas un uuid.
 */
export function swapUuidHalves(uuid: string): string | null {
  const s = uuid.trim().toLowerCase();
  if (!UUID_RE.test(s)) return null;
  const hex = s.replace(/-/g, '');
  const reverse = (h: string) => (h.match(/../g) ?? []).reverse().join('');
  const x = reverse(hex.slice(0, 16)) + reverse(hex.slice(16));
  return `${x.slice(0, 8)}-${x.slice(8, 12)}-${x.slice(12, 16)}-${x.slice(16, 20)}-${x.slice(20)}`;
}

/**
 * Uuid de joueur Bedrock dans sa forme canonique : celle que calcule le moteur depuis le XUID (MD5,
 * version 3, variante RFC 4122), clé de la ProfileApi et de LuckPerms. La forme aux octets inversés n'a
 * presque jamais ces bits à leur place : un uuid qui les a ouvre la fiche tel quel, sans recherche.
 */
export function isCanonicalPlayerUuid(uuid: string): boolean {
  const s = uuid.trim().toLowerCase();
  return UUID_RE.test(s) && s[14] === '3' && '89ab'.includes(s[19]!);
}

/** Même joueur : uuid identiques (sans casse), ou l'un est l'autre aux octets de chaque moitié inversés. */
export function isSamePlayerUuid(a: string, b: string): boolean {
  const x = a.trim().toLowerCase();
  const y = b.trim().toLowerCase();
  return x === y || (swapUuidHalves(x) ?? '') === y;
}
